import { useEffect, useRef, useState } from "react";

import {
  AUDIENCE_OVERLAP_POLLING_INTERVAL,
  AUDIENCE_OVERLAP_POLLING_MAX_ATTEMPTS,
  AUDIENCE_OVERLAP_POLLING_MAX_MISSING_STATES,
} from "./constants";
import {
  calculateCreatorsAudienceOverlap,
  CreatorsAudienceOverlapRequest,
  getCreatorsAudienceOverlapState,
} from "./creatorsAudienceOverlapApi";

export type CreatorsAudienceOverlapStatus =
  | "loading"
  | "done"
  | "failed"
  | "timeout"
  | "error";

export interface CreatorsAudienceOverlapProviderState {
  status: string;
  audienceOverlap: {
    reportInfo?: { totalFollowers: number; totalUniqueFollowers: number };
    data?: {
      username: string;
      followers: number;
      uniquePercentage: number;
    }[];
    // an empty list of errors comes from the API as an array
    errorsWithCalculatingOverlap?: Record<string, string> | unknown[];
  } | null;
}

export type CreatorsAudienceOverlapData = Record<
  string,
  CreatorsAudienceOverlapProviderState
>;

/**
 * Asks for the analysis of the given creators and polls until it is finished.
 * The request is sent once, when the component using it is mounted.
 */
export function useCreatorsAudienceOverlap(
  request: CreatorsAudienceOverlapRequest,
) {
  const [status, setStatus] =
    useState<CreatorsAudienceOverlapStatus>("loading");
  const [data, setData] = useState<CreatorsAudienceOverlapData>({});
  // the panel is opened for one selection, a different one means opening it again
  const requestRef = useRef(request);
  // a retry has to drop the failed result kept in the cache, otherwise it would be read again
  const [run, setRun] = useState({ id: 0, force: false });

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    let missingStates = 0;

    const handle = (content: {
      status: string;
      audienceOverlapData: CreatorsAudienceOverlapData;
    }) => {
      if (cancelled) return;

      if (["pending", "not_calculated"].includes(content?.status)) {
        attempts += 1;
        missingStates =
          content.status === "not_calculated" ? missingStates + 1 : 0;

        if (
          attempts >= AUDIENCE_OVERLAP_POLLING_MAX_ATTEMPTS ||
          missingStates >= AUDIENCE_OVERLAP_POLLING_MAX_MISSING_STATES
        ) {
          setStatus("timeout");

          return;
        }

        timer = setTimeout(poll, AUDIENCE_OVERLAP_POLLING_INTERVAL);

        return;
      }

      setData(content.audienceOverlapData || {});
      setStatus(content.status === "done" ? "done" : "failed");
    };

    const poll = async () => {
      try {
        handle(await getCreatorsAudienceOverlapState(requestRef.current));
      } catch (error) {
        if (!cancelled) setStatus("error");
      }
    };

    const start = async () => {
      try {
        handle(
          await calculateCreatorsAudienceOverlap({
            ...requestRef.current,
            force: run.force,
          }),
        );
      } catch (error) {
        if (!cancelled) setStatus("error");
      }
    };

    start();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [run]);

  const retry = () => {
    setStatus("loading");
    setRun((previous) => ({ id: previous.id + 1, force: true }));
  };

  return { status, data, retry };
}
